import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { dominio: "", dominio_acoplado: "", marca_modelo: "", transportista_id: "", observaciones: "" };

export default function VehiculosTab() {
  const [items, setItems] = useState([]);
  const [transportistas, setTransportistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: vs }, { data: ts }] = await Promise.all([
        api.get("/rec/vehiculos"),
        api.get("/rec/transportistas"),
      ]);
      setItems(vs || []);
      setTransportistas(ts || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.transportista_id === "__none__" || payload.transportista_id === "") {
        payload.transportista_id = null;
      }
      if (editing) {
        await api.put(`/rec/vehiculos/${editing.id}`, payload);
        toast.success("Vehículo actualizado");
      } else {
        await api.post("/rec/vehiculos", payload);
        toast.success("Vehículo creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const del = async (it) => {
    if (!confirm(`¿Eliminar vehículo dominio ${it.dominio}?`)) return;
    try {
      await api.delete(`/rec/vehiculos/${it.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Catálogo de vehículos</CardTitle>
        <Button size="sm" onClick={openNew} data-testid="new-vehiculo-btn">
          <Plus size={16} className="mr-1" /> Nuevo vehículo
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center text-neutral-500 py-8">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-neutral-400 py-12" data-testid="vehiculos-empty">
            No hay vehículos cargados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-neutral-500 border-b">
                <tr>
                  <th className="text-left py-2">Dominio</th>
                  <th className="text-left">Acoplado</th>
                  <th className="text-left">Marca / modelo</th>
                  <th className="text-left">Transportista</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b hover:bg-neutral-50" data-testid={`vehiculo-row-${it.id}`}>
                    <td className="py-2 font-mono uppercase">{it.dominio}</td>
                    <td className="font-mono uppercase">{it.dominio_acoplado || "-"}</td>
                    <td>{it.marca_modelo || "-"}</td>
                    <td>{it.transportista_nombre || <span className="text-neutral-400">-</span>}</td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(it)}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Nuevo"} vehículo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Dominio *</Label>
              <Input
                data-testid="vehiculo-dominio-input"
                value={form.dominio}
                onChange={(e) => setForm({ ...form, dominio: e.target.value.toUpperCase() })}
                placeholder="AB123CD"
              />
            </div>
            <div>
              <Label>Dominio acoplado</Label>
              <Input
                value={form.dominio_acoplado || ""}
                onChange={(e) => setForm({ ...form, dominio_acoplado: e.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label>Marca / modelo</Label>
              <Input value={form.marca_modelo || ""} onChange={(e) => setForm({ ...form, marca_modelo: e.target.value })} />
            </div>
            <div>
              <Label>Transportista</Label>
              <Select
                value={form.transportista_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, transportista_id: v })}
              >
                <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {transportistas.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre} ({t.cuit})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-vehiculo-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
